/**
 * Screen-fixed onboarding overlay.
 *
 * Renders the active onboarding step's component into a viewport-fixed
 * card via portal. Picks up which step + state to render from
 * `CanvasOnboardingContext.activeOnboarding`, which the invisible
 * `OnboardingWidget` state shell keeps fresh.
 *
 * Anchors (per-step, declared in `./steps/index.ts` via the
 * `StepDefinition.anchor` field) decide where the card docks. Splash
 * anchors center it as a hero card; compact anchors tuck it into a
 * corner so the canvas / chrome the step is talking about stays
 * usable. Anchor changes animate via CSS transitions, gated on
 * `prefers-reduced-motion`.
 *
 * Sizing model: rather than CSS-scaling a 1600×900 intrinsic layout
 * down into the anchor rect (which made small docks unreadable), the
 * overlay exposes per-variant typography + spacing tokens via CSS
 * custom properties on the frame (`--co-onb-fs-*`, `--co-onb-stack-*`,
 * `--co-onb-pad-*`). Step components read those vars instead of
 * hard-coding pixel sizes, so the same component lays out cleanly in
 * a generous splash card and a 520×293 corner coach card.
 *
 * Content is left-aligned by default (coach-card aesthetic). Splash
 * steps still render their interactive widgets centered when needed
 * (e.g. `DragMarkStep`'s logo + slot composition).
 */

import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCanvasOnboardingState } from "../../../context/CanvasOnboardingContext";
import { type AnchorRect, resolveAnchor, variantForAnchor } from "./anchors";
import { ONBOARDING_STYLES } from "./styles";

interface ViewportSize {
  width: number;
  height: number;
}

function readViewport(): ViewportSize {
  if (typeof window === "undefined") return { width: 1280, height: 800 };
  return { width: window.innerWidth, height: window.innerHeight };
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return reduced;
}

function useViewport(): ViewportSize {
  const [vp, setVp] = useState<ViewportSize>(() => readViewport());
  useEffect(() => {
    const onResize = () => setVp(readViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return vp;
}

export const OnboardingOverlay = memo(function OnboardingOverlay() {
  const { activeOnboarding, safeInsets } = useCanvasOnboardingState();
  const viewport = useViewport();
  const reducedMotion = usePrefersReducedMotion();

  // Measured height of the rendered card. Used to vertically center
  // self-centering auto-height anchors (currently `rightCenterCompact`)
  // accurately rather than against a fixed estimate — see `topOverride`.
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  // Keyed on the active step so the observer (re)attaches when the
  // overlay first mounts its card and whenever the step changes.
  const activeStep = activeOnboarding?.step;
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const measure = () => setCardHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeStep]);

  if (!activeOnboarding) return null;

  const { stepDef, data, outgoing, restartCount, step } = activeOnboarding;
  const StepComponent = stepDef.component;
  const variant = variantForAnchor(stepDef.anchor);
  const rect: AnchorRect = resolveAnchor(stepDef.anchor, viewport, safeInsets);

  // Self-centering anchors: recompute `top` from the measured card
  // height so the card centers vertically when it fits and floats to
  // the top of the safe area (keeping a minimum margin) when it's
  // taller than the viewport — never overflowing the bottom edge and
  // clipping its CTA.
  let topOverride: number | undefined;
  const measuredCenterAnchor =
    stepDef.anchor === "rightCenterCompact" ||
    stepDef.anchor === "centerSplash";
  if (measuredCenterAnchor && cardHeight != null) {
    const safeH = Math.max(
      0,
      viewport.height - safeInsets.top - safeInsets.bottom,
    );
    // Minimum breathing margin between the card and the safe-area
    // edge. The finale splash card gets a more generous one than the
    // corner coach cards so it never reads as touching the edges.
    const minVMargin = stepDef.anchor === "centerSplash" ? 32 : 0;
    topOverride =
      safeInsets.top +
      Math.max(minVMargin, Math.round((safeH - cardHeight) / 2));
  }

  return createPortal(
    <>
      <style>{ONBOARDING_STYLES}</style>
      <style>{OVERLAY_STYLES}</style>
      <div
        ref={cardRef}
        className={`_co_onb _co_onb_overlay _co_onb_overlay_${variant}${
          stepDef.anchor === "centerCompact"
            ? " _co_onb_overlay_center_compact"
            : ""
        }${
          stepDef.anchor === "rightCenterCompact"
            ? " _co_onb_overlay_right_center"
            : ""
        }${reducedMotion ? " _co_onb_overlay_no_motion" : ""}`}
        style={{
          position: "fixed",
          // Anchor by whichever edge the rect specifies. Bottom corners
          // pin `bottom` so a content-height card hugs the viewport
          // bottom; everything else pins `top`.
          ...(rect.bottom !== undefined
            ? { bottom: rect.bottom }
            : { top: topOverride ?? rect.top }),
          left: rect.left,
          width: rect.width,
          // Compact cards use "auto" so the card sizes to its content.
          // Splash cards use a fixed pixel height for the hero layout.
          ...(rect.height !== "auto" ? { height: rect.height } : {}),
          zIndex: 55,
          color: "var(--co-fg)",
          fontFamily:
            "ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Inter', 'Geist', sans-serif",
          // "overflow: hidden" would clip auto-height cards. The inner
          // content wrapper already applies overflow: hidden to clip the
          // settle-entrance animations, so the outer container doesn't
          // need it. Splash cards keep a soft clip for safety.
          overflow: rect.height === "auto" ? "visible" : "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        role="dialog"
        aria-label="Cohesive onboarding"
      >
        <div
          /*
            Step container. Composite `step:restart` key remounts the
            active step subtree on advance or dev restart so settle
            keyframes replay cleanly and step-local state resets to
            the new Loro data. Exit animation is driven by the
            `outgoing` flag from the state shell.
          */
          key={`${step}:${restartCount}`}
          className={
            outgoing
              ? "_co_onb_step_exit _co_onb_overlay_content"
              : "_co_onb_overlay_content"
          }
        >
          <StepComponent
            id={activeOnboarding.nodeId}
            data={data}
            onAdvance={activeOnboarding.advance}
            onFinish={activeOnboarding.finish}
          />
        </div>
      </div>
    </>,
    document.body,
  );
});

/**
 * Per-variant CSS custom properties consumed by `StepHeader` and the
 * individual step components (eyebrow / heading / lede / hint / meta
 * font sizes; stack spacing for vertical rhythm; padding for the
 * card edge). The two variants share names so step files can use
 * `var(--co-onb-fs-eyebrow)` etc. without knowing which variant is
 * currently active.
 *
 * These values were tuned against the rendered frame sizes in
 * `anchors.ts` (compact ~520×293, splash ~860×484 on a 1440 viewport)
 * — splash gets generous coach-card typography; compact tightens
 * everything down to a corner-card scale that still reads at a glance.
 */
const OVERLAY_STYLES = `
  ._co_onb_overlay {
    transition:
      top 320ms cubic-bezier(0.2, 0, 0, 1),
      bottom 320ms cubic-bezier(0.2, 0, 0, 1),
      left 320ms cubic-bezier(0.2, 0, 0, 1),
      width 320ms cubic-bezier(0.2, 0, 0, 1);
    will-change: top, bottom, left, width;
  }
  ._co_onb_overlay_splash {
    --co-onb-fs-eyebrow: 12px;
    --co-onb-fs-heading: 38px;
    --co-onb-fs-heading-display: 56px;
    --co-onb-fs-lede: 17px;
    --co-onb-fs-hint: 12px;
    --co-onb-fs-meta: 16px;
    --co-onb-fs-cta: 14px;
    --co-onb-stack-xs: 6px;
    --co-onb-stack-sm: 12px;
    --co-onb-stack-md: 20px;
    --co-onb-stack-lg: 32px;
    --co-onb-stack-xl: 44px;
    --co-onb-pad-x: 40px;
    --co-onb-pad-y: 36px;
    --co-onb-content-max: 560px;
    --co-onb-dot: 9px;

    /* Card chrome — frosted-glass treatment: semi-transparent paper
       fill so the canvas hints through, then a backdrop blur on the
       card so anything bleeding through stays soft and the card copy
       reads cleanly. */
    background: rgba(243, 239, 230, 0.97);
    backdrop-filter: blur(18px) saturate(1.08);
    -webkit-backdrop-filter: blur(18px) saturate(1.08);
    border: 1px solid var(--co-border);
    border-radius: 22px;
    box-shadow:
      0 1px 2px rgba(15, 12, 8, 0.06),
      0 28px 80px rgba(15, 12, 8, 0.28);
  }
  .dark ._co_onb_overlay_splash {
    background: rgba(26, 26, 22, 0.97);
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.4),
      0 32px 96px rgba(0, 0, 0, 0.6);
  }
  ._co_onb_overlay_compact {
    /* Type ramp matches the centered welcome step (step 1) so corner
       coach cards read at the same density as the opening card —
       per-step inline font overrides were removed in favor of this
       single source of truth. */
    --co-onb-fs-eyebrow: 11px;
    --co-onb-fs-heading: 28px;
    --co-onb-fs-heading-display: 38px;
    --co-onb-fs-lede: 15px;
    --co-onb-fs-hint: 11px;
    --co-onb-fs-meta: 14px;
    --co-onb-fs-cta: 13px;
    --co-onb-stack-xs: 4px;
    --co-onb-stack-sm: 8px;
    --co-onb-stack-md: 14px;
    --co-onb-stack-lg: 22px;
    --co-onb-stack-xl: 30px;
    --co-onb-pad-x: 28px;
    --co-onb-pad-y: 26px;
    --co-onb-content-max: 100%;
    --co-onb-dot: 7px;

    background: var(--co-paper);
    border: 1px solid var(--co-border);
    border-radius: 18px;
    box-shadow:
      0 1px 2px rgba(15, 12, 8, 0.06),
      0 12px 36px rgba(15, 12, 8, 0.18);
  }
  .dark ._co_onb_overlay_compact {
    background: rgba(26, 26, 22, 0.96);
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.4),
      0 16px 48px rgba(0, 0, 0, 0.55);
  }

  /* Inner content wrapper. Left-aligned coach-card layout on every
     step. Steps that have a centered interactive composition (e.g.
     drag-mark slot) opt back into centering at their own component
     level. */
  ._co_onb_overlay_content {
    flex: 1 1 auto;
    width: 100%;
    box-sizing: border-box;
    padding: var(--co-onb-pad-y) var(--co-onb-pad-x);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    text-align: left;
    gap: var(--co-onb-stack-md);
    overflow: hidden;
  }
  ._co_onb_overlay_restart {
    position: absolute;
    top: 8px;
    right: 10px;
    left: auto;
    z-index: 5;
  }
  /* Centered compact card (step 1 welcome). Sits between compact and
     splash on the type ramp — the welcome heading needs a bit more
     presence than a corner coach card without going full splash. */
  ._co_onb_overlay_center_compact {
    --co-onb-fs-eyebrow: 11px;
    --co-onb-fs-heading: 28px;
    --co-onb-fs-heading-display: 38px;
    --co-onb-fs-lede: 15px;
    --co-onb-fs-hint: 11px;
    --co-onb-fs-meta: 14px;
    --co-onb-fs-cta: 13px;
    --co-onb-pad-x: 28px;
    --co-onb-pad-y: 26px;
  }
  /* Right-docked card (step 3, drag-brief). Matches the vertical
     padding to the horizontal padding so the card edge inset is even
     on all four sides. */
  ._co_onb_overlay_right_center { --co-onb-pad-y: var(--co-onb-pad-x); }
  ._co_onb_overlay_no_motion { transition: none !important; }
  @media (prefers-reduced-motion: reduce) {
    ._co_onb_overlay { transition: none; }
  }
`;
